// components/TransactionQuantityModal.jsx
import { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Badge, Row, Col } from 'react-bootstrap';
import { FaExclamationTriangle, FaInfoCircle, FaCalculator, FaCheckCircle } from 'react-icons/fa';
import '../style/transaction-quantity-modal.css'
import FileUpload from '../FileUploads/FileUpload';
import { formatCurrency, formatCurrencys } from '../../../service/utils/facturationUtils';
import { formatAmount } from '../../../service/utils/orderUtils';
import { formatNumber } from '../../../service/utils/formatters';

const TransactionQuantityModal = ({
  show,
  onHide,
  onConfirm,
  order,
  loading = false
}) => {
   const [quantity, setQuantity] = useState('');
  const [customPrice, setCustomPrice] = useState(''); // ← NOUVEAU ÉTAT
  const [errors, setErrors] = useState({});
  const [showWarning, setShowWarning] = useState(false);


  // Ajouter dans le state
  const [preuve, setPreuve] = useState(null);

  // Ajouter le gestionnaire
  const handlePreuveChange = (file) => {
    setPreuve(file);
    setErrors(prev => ({ ...prev, preuve: undefined }));
  };


  // Réinitialiser le formulaire quand le modal s'ouvre/ferme
  useEffect(() => {
    if (show) {
      setQuantity('');
      setCustomPrice(''); // ← RÉINITIALISER
      setErrors({});
      setShowWarning(false);
    }
  }, [show]);

  if (!order) return null;

  const maxQuantity = order.quantity || 0;
  const minQuantity = order.minQuantity || 0;
  const defaultPrice = order.price || 0;

  // Utiliser le prix personnalisé s'il existe, sinon le prix par défaut
  const actualPrice = customPrice ? parseFloat(customPrice) : defaultPrice; // ← PRIX FINAL

  // Calculer les valeurs dérivées avec le prix actuel
  const calculatedAmount = quantity ? (parseFloat(quantity) * actualPrice) : 0;
  const remainingQuantity = maxQuantity - (quantity ? parseFloat(quantity) : 0);

  const validateQuantity = (value) => {
    const newErrors = {};
    const numValue = parseFloat(value);

    if (!value || value === '') {
      newErrors.quantity = 'La quantité est requise';
    } else if (isNaN(numValue) || numValue <= 0) {
      newErrors.quantity = 'La quantité doit être un nombre positif';
    } else if (numValue > maxQuantity) {
      newErrors.quantity = `La quantité ne peut pas dépasser ${maxQuantity.toLocaleString()}`;
    } else if (numValue < minQuantity) {
      newErrors.quantity = `La quantité doit être au moins ${minQuantity.toLocaleString()}`;
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }

    // Validation du prix personnalisé
    if (customPrice && customPrice !== '') {
      const priceValue = parseFloat(customPrice);
      if (isNaN(priceValue) || priceValue <= 0) {
        newErrors.price = 'Le prix doit être un nombre positif';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;
    setQuantity(value);
    validateQuantity(value);
  };

  // ← NOUVELLE FONCTION POUR LE PRIX
  const handlePriceChange = (e) => {
    const value = e.target.value;
    setCustomPrice(value);
    validateQuantity(quantity); // Re-valider pour mettre à jour les erreurs
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Valider une seule fois
    const isValidQuantity = validateQuantity(quantity);

    if (!preuve) {
      setErrors(prev => ({
        ...prev,
        preuve: 'La preuve est obligatoire pour confirmer la transaction.'
      }));
      return;
    }

    if (!isValidQuantity) return;

    onConfirm(parseFloat(quantity), actualPrice, preuve);

  };


  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="transaction-quantity-modal">
      <Modal.Header closeButton className="border-bottom-0 bg-light">
        <div className="d-flex align-items-center w-100">
          <FaCalculator className="text-primary me-3" size={24} />
          <div className="flex-grow-1">
            <Modal.Title className="h4 mb-1 text-dark">
              Créer une transaction
            </Modal.Title>
            <p className="text-muted mb-0">Ordre #{order.id} - {order.securityName}</p>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="pt-0">
        {/* Informations de l'ordre */}
        <div className="card border-0 bg-light mb-4 mt-2">
          <div className="card-body py-3">
            <Row className="g-3">
              <Col sm={6}>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Quantité maximale:</span>
                  <Badge bg="outline-primary" text="dark" className="fs-6">
                    {formatAmount(maxQuantity)}
                  </Badge>
                </div>
              </Col>
              <Col sm={6}>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Quantité minimale:</span>
                  <Badge bg="outline-warning" text="dark" className="fs-6">
                    {formatAmount(minQuantity)}
                  </Badge>
                </div>
              </Col>
              <Col sm={6}>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Prix unitaire par défaut:</span>
                  <span className="fw-semibold">{formatCurrency(defaultPrice)}</span>
                </div>
              </Col>
              <Col sm={6}>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Type de marché:</span>
                  <span className="fw-semibold text-capitalize">
                    {order.targetMarketName?.toLowerCase() || 'N/A'}
                  </span>
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* Formulaire de saisie */}
        <Form >
          {/* Champ quantité (inchangé) */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              Quantité à traiter
              <FaInfoCircle className="text-muted ms-2" size={14} />
            </Form.Label>
            
            <Form.Control
              type="number"
              value={quantity}
              onChange={handleQuantityChange}
              isInvalid={!!errors.quantity}
              placeholder={`Saisissez une quantité entre ${minQuantity} et ${maxQuantity}`}
              className="py-3 fs-6 border-2"
              min={minQuantity}
              max={maxQuantity}
              step="1"
            />
            
            {errors.quantity && (
              <Form.Control.Feedback type="invalid" className="d-flex align-items-center mt-2">
                <FaExclamationTriangle className="me-2" />
                {errors.quantity}
              </Form.Control.Feedback>
            )}

            <Form.Text className="text-muted d-block mt-2">
              Valeur acceptée: entre {formatAmount(minQuantity)} et {formatAmount(maxQuantity)}
            </Form.Text>
          </Form.Group>

          {/* ← NOUVEAU CHAMP PRIX PERSONNALISÉ */}
          <Form.Group className="mb-4">
            <Form.Label className="fw-semibold">
              Prix unitaire personnalisé (optionnel)
              <FaInfoCircle className="text-muted ms-2" size={14} />
            </Form.Label>
            
            <Form.Control
              type="number"
              value={customPrice}
              onChange={handlePriceChange}
              isInvalid={!!errors.price}
              placeholder={`Prix par défaut: ${formatCurrencys(defaultPrice)}`}
              className="py-3 fs-6 border-2"
              min="0"
              step="0.01"
            />
            
            {errors.price && (
              <Form.Control.Feedback type="invalid" className="d-flex align-items-center mt-2">
                <FaExclamationTriangle className="me-2" />
                {errors.price}
              </Form.Control.Feedback>
            )}

            <Form.Text className="text-muted d-block mt-2">
              Laissez vide pour utiliser le prix par défaut de l'ordre
            </Form.Text>

            {/* // Ajouter dans le formulaire, avant le récapitulatif */}
            <div className="mt-4">
              <FileUpload
                value={preuve}
                onChange={handlePreuveChange}
                error={errors.preuve}
                className="mb-4"
              />
            </div>
          </Form.Group>

          {/* Message d'avertissement pour la quantité minimale */}
          {showWarning && quantity && parseFloat(quantity) < minQuantity && (
            <Alert variant="warning" className="d-flex align-items-center">
              <FaExclamationTriangle className="me-2 flex-shrink-0" />
              <div>
                <Alert.Heading className="h6 mb-1">Attention: Quantité inférieure au minimum</Alert.Heading>
                <p className="mb-0 small">
                  La quantité saisie ({formatCurrency(quantity)}) est inférieure à la quantité minimale requise ({formatCurrency(minQuantity)}). 
                  Cette transaction pourrait ne pas être traitée correctement.
                </p>
              </div>
            </Alert>
          )}

          {/* Récapitulatif des calculs - MIS À JOUR AVEC LE PRIX ACTUEL */}
          {quantity && !errors.quantity && (
            <div className="card border-success bg-success bg-opacity-10 mb-4">
              <div className="card-body">
                <h6 className="text-success mb-3 d-flex align-items-center">
                  <FaCheckCircle className="me-2" />
                  Récapitulatif de la transaction
                </h6>
                <Row className="g-3">
                  <Col sm={6}>
                    <div className="d-flex justify-content-between">
                      <span>Quantité saisie:</span>
                      <strong>{formatNumber(quantity)}</strong>
                    </div>
                  </Col>
                  <Col sm={6}>
                    <div className="d-flex justify-content-between">
                      <span>Prix unitaire utilisé:</span>
                      <strong>{formatCurrency(actualPrice)}</strong>
                    </div>
                  </Col>
                  <Col sm={6}>
                    <div className="d-flex justify-content-between">
                      <span>Montant total:</span>
                      <strong className="text-success">
                        {formatCurrency(calculatedAmount)}
                      </strong>
                    </div>
                  </Col>
                  <Col sm={6}>
                    <div className="d-flex justify-content-between">
                      <span>Quantité restante:</span>
                      <strong className={remainingQuantity === 0 ? 'text-success' : 'text-warning'}>
                        {formatNumber(remainingQuantity)}
                      </strong>
                    </div>
                  </Col>
                </Row>
                {/* Indication du prix utilisé */}
                {customPrice && (
                  <div className="mt-2 text-info small">
                    <FaInfoCircle className="me-1" />
                    Utilisation du prix personnalisé
                  </div>
                )}
              </div>
            </div>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer className="border-top-0 bg-light">
        <Button 
          variant="outline-secondary" 
          onClick={onHide}
          disabled={loading}
        >
          Annuler
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit}
          disabled={
            !!errors.quantity ||
            !quantity ||
            !preuve ||
            loading
          }

        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Traitement en cours...
            </>
          ) : (
            <>
              <FaCheckCircle className="me-2" />
              Confirmer la transaction
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TransactionQuantityModal;